import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Users, BookOpen, LayoutDashboard, Info, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { PageHeader } from '../shared/PageHeader';
import { useAppStore } from '../../hooks/useAppStore';
import { getLinkedLearners } from '../../utils/accountHelpers';
import { isTeacherAccount } from '../../types/accounts';
import { AppState } from '../../types';
import { warn } from '../../utils/logger';
import { TeacherOverviewTab } from './TeacherOverviewTab';
import { TeacherStudentsTab } from './TeacherStudentsTab';
import { TeacherSubjectsTab } from './TeacherSubjectsTab';
import { TabBar } from '../shared/TabBar';
interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
type TabId = 'overview' | 'students' | 'subjects';
interface TeacherPortalPageProps {
  onNavigateToLearner?: (learnerId: string) => void;
}
export function TeacherPortalPage({
  onNavigateToLearner
}: TeacherPortalPageProps) {
  const {
    accounts,
    activeAccount,
    selectedGroup,
    getLearnerData,
    getLearnerModuleMastery,
    isInitialized,
    setViewingLearner,
    viewingLearnerId
  } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const isTeacher = activeAccount && isTeacherAccount(activeAccount);
  if (!isInitialized) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5335">
        <div className="text-center" data-id="element-5336">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" data-id="element-5337" />
          <p className="text-slate-500 font-medium" data-id="element-5338">Laden...</p>
        </div>
      </div>;
  }
  if (!isTeacher) {
    return <div className="max-w-2xl mx-auto" data-id="element-5339">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-5340">
          <Card className="p-12 text-center border-2 border-indigo-100 bg-indigo-50/30" data-id="element-5341">
            <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-indigo-100" data-id="element-5342">
              <GraduationCap className="w-10 h-10 text-indigo-600" data-id="element-5343" />
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-5344">
              Docentportaal
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed max-w-md mx-auto" data-id="element-5345">
              Dit portaal is bedoeld voor docenten om de voortgang van hun klas
              te volgen.
            </p>
            <div className="bg-white rounded-xl p-6 border-2 border-indigo-200 max-w-md mx-auto" data-id="element-5346">
              <div className="flex items-start gap-3 text-left" data-id="element-5347">
                <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-5348" />
                <div className="text-sm text-slate-700" data-id="element-5349">
                  <p className="font-bold mb-2" data-id="element-5350">Account wisselen</p>
                  <p className="text-slate-600" data-id="element-5351">
                    Je bent nu ingelogd als een ander accounttype. Gebruik de
                    account-switcher rechtsboven om naar je docentaccount te
                    wisselen.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>;
  }
  const linkedLearners = getLinkedLearners(accounts, activeAccount.id);
  const students: StudentData[] = linkedLearners.map(learner => {
    const learnerData = getLearnerData(learner.id);
    if (!learnerData) {
      warn(`No data found for student ${learner.id}`);
      return null;
    }
    return {
      id: learner.id,
      name: learner.displayName,
      data: learnerData
    };
  }).filter((s): s is StudentData => s !== null);
  const handleSelectStudent = (studentId: string) => {
    setViewingLearner(studentId);
  };
  return <div className="pb-20 space-y-8" data-id="element-5352">
      <PageHeader icon={GraduationCap} title="Mijn Klas" subtitle={`${activeAccount.classroomName || `Groep ${selectedGroup}`} • ${students.length} leerlingen`} accentColor="bg-indigo-100 text-indigo-600" data-id="element-5353" />

      {students.length === 0 ? <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-5354">
          <Card className="p-12 text-center border-2 border-amber-100 bg-amber-50/30" data-id="element-5355">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-amber-100" data-id="element-5356">
              <AlertCircle className="w-8 h-8 text-amber-600" data-id="element-5357" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-3" data-id="element-5358">
              Geen gekoppelde leerlingen
            </h3>
            <p className="text-slate-600 text-lg max-w-md mx-auto" data-id="element-5359">
              Er zijn nog geen leerling-accounts gekoppeld aan dit
              docentaccount. Koppel leerlingen om hun voortgang te volgen.
            </p>
          </Card>
        </motion.div> : <>
          {/* Tabs */}
          <TabBar tabs={[{
        id: 'overview',
        label: 'Overzicht',
        icon: LayoutDashboard
      }, {
        id: 'students',
        label: 'Leerlingen',
        icon: Users
      }, {
        id: 'subjects',
        label: 'Vakken',
        icon: BookOpen
      }]} activeTab={activeTab} onTabChange={setActiveTab} variant="indigo" data-id="element-5360" />

          {/* Content */}
          <AnimatePresence mode="wait" data-id="element-5361">
            <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-5362">
              {activeTab === 'overview' && <TeacherOverviewTab students={students} selectedGroup={selectedGroup} getModuleMastery={getLearnerModuleMastery} data-id="element-5363" />}
              {activeTab === 'students' && <TeacherStudentsTab students={students} selectedGroup={selectedGroup} getModuleMastery={getLearnerModuleMastery} viewingLearnerId={viewingLearnerId} onSelectStudent={handleSelectStudent} onNavigateToLearner={onNavigateToLearner} data-id="element-5364" />}
              {activeTab === 'subjects' && <TeacherSubjectsTab students={students} selectedGroup={selectedGroup} getModuleMastery={getLearnerModuleMastery} data-id="element-5365" />}
            </motion.div>
          </AnimatePresence>
        </>}
    </div>;
}
function TabButton({
  id,
  label,
  icon: Icon,
  active,
  onClick
}: {
  id: string;
  label: string;
  icon: any;
  active: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`
        flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap
        ${active ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
      `} data-id="element-5366">
      <Icon className="w-4 h-4" data-id="element-5367" />
      {label}
    </button>;
}